import { ButtonV2 } from '@cimplico/components';
import { FloatingPortal, autoUpdate, flip, useFloating, hide, shift, useFloatingNodeId, FloatingNode, offset } from '@floating-ui/react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ReactNode, useCallback, useEffect, useRef } from 'react';

type CWInputControlWrapperProps = {
    zIndex: number;
    children: ReactNode | ReactNode[];
    onSave: (resetEditingField?: boolean) => void;
    onBlur?: (resetEditingField?: boolean) => void;
    onCancel: () => void;
    hideButtons?: boolean;
};

function CWInputControlWrapper({
    zIndex,
    children,
    onSave,
    onBlur,
    onCancel,
    hideButtons = false,
}: CWInputControlWrapperProps): JSX.Element {
    const outerDivRef = useRef<HTMLDivElement | null>(null);

    const nodeId = useFloatingNodeId();

    const { refs, floatingStyles, middlewareData } = useFloating({
        nodeId,
        placement: 'bottom-end',
        middleware: [flip(), shift(), offset(4), hide()],
        whileElementsMounted: autoUpdate,
    });

    /**
     * Revert changes on input value on 'Escape' key clicked
     * Save changes on input value on 'Enter' key clicked
     */
    const handleKeyboardShortcut = useCallback(
        function handleKeyboardShortcut(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                onCancel();
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                onSave();
            }
        },
        [onSave, onCancel]
    );

    /**
     * When user clicks outside of the input area, blurs the input and save updated value
     */
    const handleClickOutside = useCallback(
        function handleClickOutside(event: MouseEvent): void {
            if (!(event.target instanceof Element)) {
                return;
            }

            // If click event is within outerDiv area that wraps input and action buttons, don't clear edit layout
            if (outerDivRef?.current && (outerDivRef.current.contains(event.target) || outerDivRef.current.isEqualNode(event.target))) {
                return;
            }

            // If click event is out of outerDiv area that wraps input and action buttons
            if (outerDivRef?.current && !outerDivRef.current.contains(event.target)) {
                if (event.target instanceof Element) {
                    // Check if click event moved to another edit field area
                    // Find the closest parent element that has data attribute 'data-field'
                    // If found an ancestor with the attribute, it means that the new click event is within the edit field area
                    // Therefore, don't dispatch setEditField(undefined) to allow sliding animation from one navItem to another
                    const ancestor = event.target.closest('[data-field]');
                    const attribute = ancestor ? ancestor.getAttribute('data-field') : null;

                    if (attribute) {
                        onBlur ? onBlur(false) : onSave(false);
                        return;
                    }
                }
                onBlur ? onBlur() : onSave();
            }
        },
        [onSave, onBlur]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return (): void => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardShortcut);
        return (): void => document.removeEventListener('keydown', handleKeyboardShortcut);
    }, [handleKeyboardShortcut]);

    return (
        <FloatingNode id={nodeId}>
            <div ref={outerDivRef} className="py-0.5 flex gap-2 w-full items-start relative" style={{ zIndex }}>
                <div className="grow" ref={refs.setReference}>
                    {children}
                </div>
                {!hideButtons && (
                    <FloatingPortal>
                        <div
                            onMouseDown={(e): void => {
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                            className="flex gap-1"
                            ref={refs.setFloating}
                            style={{ ...floatingStyles, zIndex, visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible' }}
                        >
                            <ButtonV2 type="secondary" leftIcon={<CheckIcon className="text-green-500" />} onClick={onSave} />
                            <ButtonV2 type="secondary" leftIcon={<XMarkIcon className="text-red-500" />} onClick={onCancel} />
                        </div>
                    </FloatingPortal>
                )}
            </div>
        </FloatingNode>
    );
}

export default CWInputControlWrapper;
